/**
 * SubscriptionAlertBanner — Tonal banner used by:
 *   - Settings → Subscription header (canceled_active, past_due, expired)
 *   - Sticky app-wide banner during past_due / suspended (Phase 8 — SOS-335)
 *
 * Tone-driven styling: amber (warning), red (urgent), neutral (info).
 * Parameterized — never assumes a specific copy / subscription state.
 * Render-prop friendly: pass any node as the CTA.
 */

import React from 'react';
import { AlertTriangle, Info, Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AlertTone = 'amber' | 'red' | 'neutral';

interface SubscriptionAlertBannerProps {
  tone: AlertTone;
  title: string;
  body: string;
  cta?: React.ReactNode;
  /** Optional icon override; defaults to AlertTriangle (amber/red) or Info (neutral). */
  icon?: React.ReactNode;
  className?: string;
}

const TONE_STYLES: Record<AlertTone, { bg: string; border: string; text: string; iconColor: string }> = {
  amber:   { bg: 'bg-amber-500/10',  border: 'border-amber-500/30',  text: 'text-amber-200',   iconColor: 'text-amber-400' },
  red:     { bg: 'bg-[#D72E16]/10',  border: 'border-[#D72E16]/40',  text: 'text-[#FFB4A8]',  iconColor: 'text-[#D72E16]' },
  neutral: { bg: 'bg-white/5',       border: 'border-white/10',      text: 'text-white/80',    iconColor: 'text-white/60' },
};

const DEFAULT_ICON: Record<AlertTone, React.ReactNode> = {
  amber:   <Clock size={18} />,
  red:     <AlertTriangle size={18} />,
  neutral: <Info size={18} />,
};

export const SubscriptionAlertBanner: React.FC<SubscriptionAlertBannerProps> = ({
  tone,
  title,
  body,
  cta,
  icon,
  className,
}) => {
  const styles = TONE_STYLES[tone];
  return (
    <div
      role="status"
      className={cn(
        'rounded-xl border p-4 flex items-start gap-3',
        styles.bg,
        styles.border,
        className,
      )}
    >
      <div className={cn('flex-shrink-0 mt-0.5', styles.iconColor)}>
        {icon ?? DEFAULT_ICON[tone]}
      </div>
      <div className="flex-1 min-w-0 space-y-1">
        <p className={cn('text-sm font-semibold', styles.text)}>{title}</p>
        <p className={cn('text-xs leading-relaxed', styles.text, 'opacity-90')}>{body}</p>
        {cta && <div className="pt-2">{cta}</div>}
      </div>
    </div>
  );
};
